import {
  faArrowLeft,
  faBell,
  faComments,
  faEllipsisVertical,
  faMagnifyingGlass,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import TopbarSearch from "../Sidebar/TopbarSearch";
import ChartMobileSearch from "../chart/ChartMobileSearch";
import i18n from "i18next";
import {
  chatList,
  getAllPost,
  getGameFromSlug,
  getNotifications,
  getProfile,
  getProfileImage,
  searchStocksApi,
} from "../../api/user";
import { toast } from "react-toastify";
import { isLeagueDetailsPath } from "../../utils/leagueCreationRoutes";
import { shareLink } from "../../utils/shareLink";
import { leagueShareData } from "../../utils/shareMessage";
import {
  isWatchlistDetailOpen,
  subscribeWatchlistMobileScreen,
} from "../../utils/watchlistMobileScreen";
import { ShareUpIcon } from "../../pages/Game/landing/icons";
import { sidebarToggle } from "../../utils/toggler";
import { MdOutlineShare } from "react-icons/md";
import { getMenuNav, clearMenuNav } from "../../utils/menuNav";
import { countUnreadThreads } from "../../utils/unreadBadges";
import useLeagueContext from "../../utils/useLeagueContext";
import {
  isDirectMessagePath,
  isLeagueChatPath,
  isLeagueNotificationsPath,
  leagueDashboardPath,
  isStandaloneLeaderboardPath,
  leagueSlugFromPath,
} from "../../utils/leaguePath";

function DashboardHeader({
  searchVal,
  toggle,
  setStocksData,
  setLoader,
  game,
  searchVal1,
  onFeedSearch,
  feedSearchActive,
  // The competition this header should offer to SHARE, when the route cannot
  // work it out for itself.
  //
  // /leaderboards/<slug>/details is deliberately not a league path —
  // leagueSlugFromPath does not resolve it, so the app shell stays QUSHi's own
  // — which also means `leagueContextGame` is null there and the mobile share
  // icon had nothing to build a message from, so it did not render at all. That
  // left one of the two preview addresses without the share the client asked
  // every landing header to carry.
  //
  // Passing the game in is the narrow fix: LeagueDetails has already resolved it,
  // and this changes nothing about which routes turn the league shell on.
  shareGame = null,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const game_id = null;
  const pathSegments = window.location.pathname.split("/");
  const isSlugPostsPath = location.pathname.includes("/posts");
  const isTradingDetail = /^\/trading-detail\/\d+\/\d+/.test(location.pathname);
  // const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(false);
  // const hideOnPaths = ["/contest", "/contest/my-games", "/contest/add-game","/contest/game-detail/:id"];
  // const shouldHideTopbarSearch = hideOnPaths.includes(location.pathname);

  const validPaths = ["feeds", "buysell", "charts", "portfolio", "leaderboard"];
  // Extract the second segment of the path
  const pathSegment = location.pathname.split("/")[2];

  // Check if the second segment matches any of the valid paths
  const isValidPath = validPaths.includes(pathSegment);

  // The Chats glyph is dropped on the Chats screens themselves — it points at
  // where the user already is. (Client note: "I removed the redundant chats
  // glyph in the top right when on the Chats screen.")
  // …and on the in-league copy of the Chats screen too (client Rev, desktop:
  // Chats opened from inside a league now renders at /contest/<slug>/chat).
  // isDirectMessagePath covers a conversation at BOTH of its addresses, so the
  // league's /<slug>/chatHistory/<id> now drops the glyph exactly like the main
  // app's /chatHistory/<id> already did — part of "the design, layout, spacing,
  // and header treatment should match the main QUSHi version".
  const isChatsScreen =
    location.pathname === "/chat" ||
    isDirectMessagePath(location.pathname) ||
    /^\/contest\/[^/]+\/chat$/.test(location.pathname);

  // The mirror of that rule, for the pair of screens the client asked about:
  // "Notifications open: show the Chats icon in the top right. / Chats open:
  // show the Notifications icon in the top right."
  //
  // The Chats half already worked — `isChatsScreen` above covers the in-league
  // address too, so /contest/<slug>/chat already drops its own glyph and leaves
  // the bell. The Notifications half had no such rule, so the bell rendered on
  // the Notifications screen pointing at the screen the user was already on.
  //
  // Scoped to the IN-LEAGUE address only. The main app's own /notifications is
  // not part of this request and keeps the pair it has always shown; widening
  // the rule to it would change a screen the client did not ask about.
  const isLeagueNotificationsScreen = isLeagueNotificationsPath(
    location.pathname
  );

  // The Notifications glyph belongs to YOUR chrome, so on a profile screen it
  // only renders on your own profile — never while you're looking at someone
  // else's. Own profile = /profile, or /profile/:id where :id is you, and not
  // the "view as public" preview of your own page.
  const isProfileScreen = pathSegments[1] === "profile";
  const isOwnProfileScreen =
    isProfileScreen &&
    !new URLSearchParams(location.search).get("view") &&
    (!pathSegments[2] || pathSegments[2] === localStorage.getItem("user_id"));

  const hideOnPaths = [
    "/search", // Watchlist page has its own in-content asset dropdown + search
    "/oldsearch", // Legacy watchlist comparison route: same chrome as /search
    "/create-post", // Create Post page: no top-bar search
    "/market-sentiment", // Market Sentiment page: no top-bar search
    "/auth/trending-stocks", // Today's Movers page: no top-bar search
    "/trading-floor", // The Edge / Trading Floor page: no top-bar search
    "/contest", // League Play page: no top-bar search
    "/settings", // Settings page: no top-bar search
    "/profile", // Private profile page (/profile): remove the nonfunctional top-bar search
    /^\/profile\/[^/]+$/, // Public profile page (/profile/:userId): hide the same nonfunctional search
    "/edit-profile", // Settings sub-page: no top-bar search
    "/change-password", // Settings sub-page (Reset password): no top-bar search
    "/saved-post", // Settings sub-page (Saved): no top-bar search
    "/notifications", // Settings sub-page (Notifications): no top-bar search
    "/guide", // Settings sub-page (Page tips): no top-bar search
    "/get-help", // Settings sub-page (Get help): no top-bar search
    "/about-qushi", // Settings sub-page (About QUSHi): no top-bar search
    /^\/performance\/[^/]+$/, // Performance page ("The Record" chart click): no top-bar search
    "/chat", // Chats inbox: the top-bar search is the watchlist search, not a chat search
    /^\/chatHistory(\/[^/]+)*$/, // Chats conversation (/chatHistory and /chatHistory/:id[/:gid]): same watchlist search
    //"/contest",
    //"/contest/my-games",
    // "/contest/add-game",
    //  /\/contest\/game-detail\/\d+/ // Use regex to match game-detail paths with numeric IDs
  ];

  const pathSegments2 = location.pathname.split("/");
  // const shouldHideForm =
  //   pathSegments2[1] === "buysell" && (!pathSegments2[2] || /^\d+$/.test(pathSegments2[2])) ||
  //   pathSegments2[1] === "charts" || pathSegments2[1] === "search";


  const shouldHideForm =
  pathSegments2[1] === "buysell" ||
  pathSegments2[1] === "charts" ||
  pathSegments2[1] === "search" ||
  pathSegments2[1] === "oldsearch" ||
  pathSegments2[1] === "chartSearch";


  // Check if the current path should hide the top bar search
  const shouldHideTopbarSearch = hideOnPaths.some((route) => {
    if (typeof route === "string") {
      return route === location.pathname; // Exact match for string paths
    } else if (route instanceof RegExp) {
      return route.test(location.pathname); // Check against regex for dynamic paths
    }
    return false;
  });

  const [scButton, setScButton] = useState(false);
  const [selectedLanguageOption, setSelectedLanguageOption] = useState("en");

  const userInfo = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : "";

  const [number, SetNumber] = useState(
    localStorage.getItem("notificationCount")
      ? localStorage.getItem("notificationCount")
      : userInfo?.device_info?.badge
  );

  const [messageCount, setMessageCount] = useState(
    localStorage.getItem("messageCount")
      ? localStorage.getItem("messageCount")
      : userInfo?.device_info?.message_count
  );

  const [avtar, setAvtar] = useState();

  const [search, setSearch] = useState(searchVal ? searchVal : "");
  const [isDataFeteched, setisDataFeteched] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  // Members only: someone previewing a league is still in the main app.
  // `game` as well as membership: the share glyph needs the competition's NAME
  // for the message the client specified, and it is shown on the pre-join
  // landing too, where isMember is false but the league still resolves.
  const { isMember: inLeagueShell, game: leagueContextGame } = useLeagueContext();
  const leagueSlug = leagueSlugFromPath(location.pathname);
  // League Home is /contest/<slug> with nothing after it. Everything else inside
  // a joined league loses the dots; the main app is untouched, because this is
  // gated on being a member. Declared here rather than further down because
  // `isPreJoinLanding` now reads it.
  const isLeagueHome =
    !!leagueSlug && location.pathname.replace(/\/+$/, "") === `/contest/${leagueSlug}`;
  const hideLeagueThreeDot = inLeagueShell && !isLeagueHome;

  // ── The mobile League Play header, locked (client, Mantis 0014787 + 0014788) ──
  //
  // The report that started it: "Today I was testing the watchlist on mobile, and
  // when I got into a specific watchlist, I was stuck; I couldn't get out. So I
  // started to look at the headers inside League Play, and i found some
  // inconsistencies."
  //
  // The spec, with 0014788's correction applied (the client wrote "note 0014787
  // was incorrect" about League Home, which had said Back arrow):
  //
  //   League Home        League avatar          | logo | Notifications, Chats, 3-dot
  //   Portfolio          League profile avatar  | logo | Notifications, Chats
  //   Watchlist home     League profile avatar  | logo | Notifications, Chats
  //   Opened watchlist   BACK ARROW             | logo | Notifications, Chats
  //   Leaderboard        League profile avatar  | logo | Notifications, Chats
  //   Trade              League profile avatar  | logo | Search only
  //   League Dashboard   BACK ARROW             | logo | Notifications, Chats
  //   League Profile     BACK ARROW             | logo | Notifications, Chats
  //
  // League Dashboard and League Profile are the same route — /<slug>/profile/<id>
  // with your own id or someone else's — which is why the client wrote one rule
  // for both. "The back arrow returns the user to the league screen they came
  // from", so it is navigate(-1) and not a fixed destination.
  //
  // EVERYTHING here is gated on `inLeagueMobile`: a member, on a phone, inside a
  // league. The main site keeps the header it has, and so does a visitor's
  // pre-join league landing — that screen still needs its back arrow to leave.
  const inLeagueMobile = isMobileView && inLeagueShell;
  const isLeagueProfilePath = /^\/[^/]+\/profile\/[^/]+$/.test(location.pathname);
  const isLeagueWatchlistPath = /^\/[^/]+\/watchlist$/.test(location.pathname);
  const isLeagueTradePath = /^\/[^/]+\/trade$/.test(location.pathname);

  // Notifications and Chats opened from INSIDE a league. Client, after testing
  // the first pass: "You kind of get stuck when you open Notifications or Chats.
  // Follow this, please, for the headers — when Notifications is open: Left: Back
  // arrow, Center: QUSHi logo, Right: Chats icon. When Chats is open: Left: Back
  // arrow, Center: QUSHi logo, Right: Notifications icon. I would remove the
  // avatar from both."
  //
  // Scoped to the in-league addresses (/contest/<slug>/notifications and
  // /contest/<slug>/chat) via the predicates leaguePath already exports. The main
  // app's own /notifications and /chat are a different screen the client did not
  // ask about, and they already carry a back arrow of their own.
  const isLeagueMessagingPath =
    isLeagueNotificationsPath(location.pathname) ||
    isLeagueChatPath(location.pathname);

  const isLeagueTabPath =
    isLeagueWatchlistPath ||
    isLeagueProfilePath ||
    isLeagueMessagingPath ||
    /^\/[^/]+\/(portfolio|leaderboard)$/.test(location.pathname) ||
    // Client: "Everything checks out except the league portfolio screen. It is
    // missing the notifications and chats icons."
    //
    // There are TWO league portfolio routes — /<slug>/portfolio (GamePortfolio)
    // and /contest/<slug>/portfolio (LeaguePortfolio, the revamped screen the
    // client was actually on). The first pass only matched the first, so the
    // second kept the empty right-hand side.
    /^\/contest\/[^/]+\/portfolio$/.test(location.pathname);

  // Opening a list on mobile does not change the route, so the header is told
  // about it — see utils/watchlistMobileScreen.
  const [watchlistDetailOpen, setWatchlistDetailOpen] = useState(
    isWatchlistDetailOpen
  );
  useEffect(
    () =>
      subscribeWatchlistMobileScreen(() =>
        setWatchlistDetailOpen(isWatchlistDetailOpen())
      ),
    []
  );

  // The screens the client gave a back arrow to. Notifications and Chats joined
  // the list after the first pass — "I would remove the avatar from both", and a
  // back arrow is what replaces it.
  const leagueMobileWantsBack =
    inLeagueMobile &&
    ((isLeagueWatchlistPath && watchlistDetailOpen) ||
      isLeagueProfilePath ||
      isLeagueMessagingPath);
  // …and the one the correction TOOK a back arrow away from.
  const leagueMobileWantsAvatar = inLeagueMobile && isLeagueHome;

  // THE LEADERBOARD PREVIEW. Client, standardising the League Play header:
  // "Remove the avatar from the top-left (crossed out). Put the standard back
  // arrow in the avatar's place, top-left in the header. Keep the QUSHi logo
  // centered. No share icon — leaderboards are not shareable yet."
  //
  // /leaderboards/<slug> is in none of the path lists below, so its left slot
  // fell through to the default avatar. It is declared on its own rather than
  // added to that list because it must ALSO suppress the avatar, and the list
  // only turns the arrow on. Not gated on membership: the preview is a screen a
  // non-member reaches, which is the whole point of it.
  const isLeaderboardPreview = isStandaloneLeaderboardPath(location.pathname);

  // THE LANDING PAGE PREVIEW — /contest/<slug>/details and its non-member twin
  // /leaderboards/<slug>/details.
  //
  // Client: "Mobile is the arrow QUSHi logo and share icon." That is the landing
  // page's own header, which is what this screen now shows the rest of: the
  // focused wizard header it used to draw has been removed (see LeagueDetails),
  // so this is the only header on it. Both addresses, so the preview a member
  // opens and the one a visitor opens carry the same bar.
  const isLandingPreview = isLeagueDetailsPath(location.pathname);

  // Client Rev, desktop: "The bell and chat icons can check one simple condition:
  // Is the user currently inside a league? If no, keep the exact existing
  // behavior… If yes, open the same existing Notifications or Chats content while
  // preserving the current League shell and current league ID. When the user
  // closes or goes back, return them to the league screen they came from."
  //
  // That is exactly this — one condition, two hrefs, nothing else. The content is
  // the SAME <Notifications /> and <ChatPage /> the main app renders; the only
  // difference is the address it renders at.
  //
  // Why the address is enough: the shell decides what it is from the URL.
  // leagueSlugFromPath already treats /contest/<slug>/… as inside a league, so
  // routing there keeps `inLeague` true and the left rail stays the league's own
  // menu with this league's ID — no state to thread, no provider to add.
  //
  // And "return them to the league screen they came from" needs no work either:
  // these are ordinary route pushes, so the browser's native Back button already
  // walks back to the exact league screen the user pressed the bell on.
  const notificationsHref =
    inLeagueShell && leagueSlug
      ? `/contest/${leagueSlug}/notifications`
      : "/notifications";
  const chatsHref =
    inLeagueShell && leagueSlug ? `/contest/${leagueSlug}/chat` : "/chat";

  // Client Rev 2, global League Play rule 2: "Tapping the top-left avatar opens
  // THAT USER'S PROFILE FOR THE CURRENT LEAGUE, never their main QUSHi profile.
  // I see you have a league profile screen, so point it there."
  //
  // Same class of bug as the league feed's post avatars and the rail's Back to
  // QUSHi card: a control rendered inside a league that quietly walked the member
  // out of it. `/:slug/profile/:user_id` is the league profile screen
  // (GameProfile); outside a league this stays the main /profile it always was.
  //
  // Falls back to /profile if either half is missing rather than building a
  // broken path — a slug with no id would land on the league's own home screen.
  const goToMyProfile = () => {
    if (inLeagueShell && slug) {
      try {
        const me = JSON.parse(localStorage.getItem("userInfo") || "null");
        if (me?.id) {
          navigate(`/${slug}/profile/${me.id}`);
          return;
        }
      } catch (e) {
        // fall through to the main profile
      }
    }
    navigate("/profile");
  };
  // Mobile chart-screen inline symbol search (magnifying glass in the top bar).
  const [chartSearchOpen, setChartSearchOpen] = useState(false);
  // Full (keyboard-down) viewport height, captured at page load. Used to detect
  // when the on-screen keyboard is up.
  const viewportBaselineRef = useRef(
    typeof window !== "undefined"
      ? (window.visualViewport ? window.visualViewport.height : window.innerHeight)
      : 0
  );
  // With interactive-widget=resizes-content the keyboard shrinks the viewport and
  // shoves the fixed-bottom trade toolbar + bottom nav up into the middle. So
  // while the chart search is open we watch the viewport height and hide those
  // two bars ONLY while the keyboard is actually up (height dropped well below the
  // baseline). Keyboard down → bars visible at the bottom; keyboard up → hidden
  // (not floating in the middle); they return the moment the keyboard closes.
  useEffect(() => {
    if (!chartSearchOpen) {
      document.body.classList.remove("chart-search-open");
      return undefined;
    }
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    const check = () => {
      const h = vv ? vv.height : window.innerHeight;
      if (h > viewportBaselineRef.current) viewportBaselineRef.current = h; // track full height
      const keyboardUp = h < viewportBaselineRef.current - 150;
      document.body.classList.toggle("chart-search-open", keyboardUp);
    };
    check();
    const target = vv || window;
    target.addEventListener("resize", check);
    return () => {
      target.removeEventListener("resize", check);
      document.body.classList.remove("chart-search-open");
    };
  }, [chartSearchOpen]);
  // The other half of the league Trade chart fix (client Rev 3, mobile inside a
  // private league: "Huge whitespace gap at the top"). This flag sizes the spacer
  // that sits under the fixed header on a chart screen — pt-12 (48px) for a chart,
  // pt-16 (64px) for an ordinary screen. /<slug>/trade IS the chart screen, but
  // the strict `=== "/charts"` never matched it, so it got the ordinary 64px.
  // With the 64px margin in PageHeaderTitle, that was the 80px gap the client saw.
  // `isChartsPath` a few hundred lines below already recognises /trade for the
  // same reason; this one had been missed.
  const isChartsRoute =
  location.pathname === "/charts" ||
  location.pathname.endsWith("/trade") ||
  location.pathname.startsWith("/chartSearch/");
  const [apiCalled, setApiCalled] = useState(false);
  const [category, setCategory] = useState("Stocks");

  const fetchProfile = async (userInfo) => {
    try {
      let response;
      if (userInfo) {
        response = await getProfileImage(userInfo.id, game_id);
      }
      if (response?.data?.data) {
        const profileData = response.data.data;
        if (profileData) {
          localStorage.setItem("userInfo", JSON.stringify(profileData));
          localStorage.setItem("img", profileData.image);
          setAvtar(
            localStorage.getItem("img") ? localStorage.getItem("img") : ""
          );
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  //   useEffect(() => {

  //     const checkGameDates = (start_date, end_date) => {

  //         const endDate = new Date(end_date.replace(" ", "T")); // Parse game.end_date
  //         const startDate = new Date(start_date.replace(" ", "T")); // Parse game.start_date
  //         const today = new Date(); // Get today's date
  //         today.setHours(0, 0, 0, 0); // Set time to midnight to ignore time component

  //         if (today >= startDate && today <= endDate) {
  //             return true;
  //         } else {
  //             return false;
  //         }

  //     }

  //     const fetchGameId = async (slug) => {
  //         try {
  //             const response = await getGameFromSlug({ slug });
  //             const gameData = response?.data?.data;
  //             if (gameData) {
  //                 //if(checkGameDates(gameData.start_date, gameData.end_date)){
  //                     setGame(gameData);
  //                     setLoading(true);
  //               //  }
  //               //  else{
  //                   //  toast.error("League is not active");
  //                     // navigate("/contest/my-games");
  //                     // navigate("/contest/my-games", { state: { viewMode: "creator" } });
  //               //  }
  //             } else {
  //                 // toast.error("League not found");
  //                 // navigate("/contest/my-games");
  //                 // navigate("/contest/my-games", { state: { viewMode: "member" } });
  //             }
  //         } catch (error) {
  //             console.error("Error fetching game ID:", error);
  //         }
  //     };

  //     fetchGameId(slug);

  // }, [slug]);

  useEffect(() => {
    localStorage.setItem("searchItem", search);
  }, [search]);

  useEffect(() => {
    // setAvtar(userInfo?.image);
    // localStorage.setItem("img", userInfo?.image);
    fetchProfile(userInfo);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleCustomProfileEvent = (event) => {
      setAvtar(event?.detail?.profileImg);
    };
    window.addEventListener("profileEvent", handleCustomProfileEvent);
    return () =>
      window.removeEventListener("profileEvent", handleCustomProfileEvent);
  }, []);

  useEffect(() => {
    const handleCustomEvent = (event) => {
      setMessageCount(event.detail.messageCount);
      SetNumber(event.detail.notificationCount);
    };
    window.addEventListener("myCustomEvent", handleCustomEvent);
    return () =>
      window.removeEventListener("myCustomEvent", handleCustomEvent);
  }, []);

  // Poll for new notification/message badge counts every 30 seconds
  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    if (!userInfo?.id) return;

    const pollBadgeCounts = async () => {
      // Nothing to update while the tab is in the background, and the badges are
      // re-derived on the next visible tick anyway. Keeps the (now three-call)
      // poll off idle tabs.
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        // Run the API calls in parallel for speed
        const [profileResult, notifResult, chatResult] =
          await Promise.allSettled([
            getProfileImage(userInfo.id, null),
            getNotifications(1, 1),
            chatList(1),
          ]);

        // Chats unread. The chat list is the authority the Chats screen itself
        // renders, so the header dot is derived from exactly the same predicate
        // as the row dots (see utils/unreadBadges) — the two cannot disagree.
        // The account-level server badge is kept as a floor so a brand-new
        // session still lights up before the list has been fetched.
        let chatUnread = null;
        if (chatResult.status === "fulfilled") {
          const threads = chatResult.value?.data?.data?.data;
          if (Array.isArray(threads)) {
            chatUnread = countUnreadThreads(threads, userInfo.id);
          }
        }

        // Update message count AND notification badge from profile image API
        if (profileResult.status === "fulfilled") {
          const data = profileResult.value?.data?.data;
          if (data) {
            // Update message count. When we have the list, it wins (it knows
            // what has been opened); otherwise fall back to the server badge.
            const newMsgCount =
              chatUnread != null ? chatUnread : data.message_count ?? 0;
            const currentMsgCount =
              parseInt(localStorage.getItem("messageCount")) || 0;
            if (newMsgCount !== currentMsgCount) {
              localStorage.setItem("messageCount", newMsgCount);
              setMessageCount(newMsgCount);
            }

            // Update notification badge directly from server
            const serverBadge = parseInt(data.badge) || 0;
            const currentNotifCount =
              parseInt(localStorage.getItem("notificationCount")) || 0;
            if (serverBadge !== currentNotifCount) {
              localStorage.setItem("notificationCount", serverBadge);
              SetNumber(serverBadge);
            }
          }
        } else if (chatUnread != null) {
          const currentMsgCount =
            parseInt(localStorage.getItem("messageCount")) || 0;
          if (chatUnread !== currentMsgCount) {
            localStorage.setItem("messageCount", chatUnread);
            setMessageCount(chatUnread);
          }
        }

        // Also set baseline for lastSeenNotificationId so future polls work
        if (notifResult.status === "fulfilled") {
          const firstNotif =
            notifResult.value?.data?.data?.data?.[0];
          if (firstNotif) {
            const lastSeenId =
              parseInt(localStorage.getItem("lastSeenNotificationId")) || 0;
            if (lastSeenId === 0) {
              // First poll — set baseline for future new-notification detection
              localStorage.setItem("lastSeenNotificationId", firstNotif.id);
            } else {
              // Detect brand-new notifications that arrived since last check
              const hasNew = firstNotif.id > lastSeenId;
              if (hasNew) {
                const currentCount =
                  parseInt(localStorage.getItem("notificationCount")) || 0;
                if (currentCount === 0) {
                  localStorage.setItem("notificationCount", 1);
                  SetNumber(1);
                }
              }
            }
          }
        }
      } catch (error) {
        // Silent fail - polling should not disrupt UX
      }
    };

    pollBadgeCounts(); // Run immediately on mount
    const intervalId = setInterval(pollBadgeCounts, 30000); // Poll every 30 seconds
    // Coming back to the tab refreshes the badges straight away rather than
    // leaving a stale dot for up to 30s.
    const onVisibilityChange = () => {
      if (!document.hidden) pollBadgeCounts();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!isDataFeteched) searchAPi();
    if (
      window.location.pathname === "/search" ||
      window.location.pathname === "/auth/trending-stocks" ||
      window.location.pathname !== "/charts" ||
      // window.location.pathname !== "/buysell" ||
      location.pathname.startsWith("/buysell") ||
      location.pathname !== `/${slug}/buysell` ||
      location.pathname !== `/${slug}/charts`
    ) {
      setSearch("");
    } else if (
      window.location.pathname === "/feed" ||
      window.location.pathname === `/${slug}/feeds`
    ) {
      setSearch("");
    } else if (window.location.pathname.includes("/feeds")) {
      // Handle slug/posts path
      setSearch("");
    }
  }, [isDataFeteched]);
  const searchRef = useRef("");

  const handleChange = (e) => {
    if (!shouldHideForm) {
      setSearch(e.target.value);
    } else {
      // alert('in change');
      // alert(e);

      const value = e;
      searchRef.current = value;
      setSearch(value);
    }
  };

  const handleCategory = (cat) => {
    setCategory(cat);
  };

  const handleSubmit = async (e) => {
    const query = searchRef.current;
    // alert("for desktop");
    // alert(query);
    //  e.preventDefault();

    // this too added beacuse of watchlist
    if (window.location.pathname === "/search") {
      setApiCalled(true);
      setLoader(true);
      const res1 = await searchStocksApi({
        search: query.toUpperCase() || "",
        category: category,
      });
      if (res1 && res1?.data.status === 200) {
        setisDataFeteched(true);
        setStocksData(res1?.data?.data);
      } else if (res1?.data?.data.length === 0) {
        toast.error("No matching assets.");
      } else {
        toast.error("Could not search right now. Try again.");
      }
      setApiCalled(false);
      setLoader(false);
    } else {
      searchAPi();

      if (search && window.location.pathname === "/leaderboard") {
        navigate(`/leaderboard?q=${encodeURIComponent(search)}`);
        return;
      }

      // if (
      //   search &&
      //   window.location.pathname !== "/feed" &&
      //   !window.location.pathname.includes("/feeds") &&
      //   window.location.pathname !== "/search" &&
      //   window.location.pathname !== "/charts" &&
      //   window.location.pathname !== "/buysell" &&
      //   location.pathname !== `/${slug}/buysell` &&
      //   location.pathname !== `/${slug}/charts`
      // ) {
      //   navigate(`/search/${search}`);
      // }

      if (
        search &&
        window.location.pathname !== "/feed" &&
        !window.location.pathname.includes("/feeds") &&
        !window.location.pathname.startsWith("/search") && // <-- handles both "/search" and "/search/CSCO"
        window.location.pathname !== "/charts" &&
        window.location.pathname !== "/buysell" &&
        !window.location.pathname.startsWith("/buysell/") && // just in case
        location.pathname !== `/${slug}/charts` &&
        !window.location.pathname.startsWith("/chartSearch")
      ) {
        navigate(`/search/${search}`);
      }
      else if (

          search &&
          window.location.pathname.startsWith("/chartSearch")
        ) {
            // ✅ Get ticker from localStorage
            const storedTicker = localStorage.getItem("selectedTicker");

            // If we have one, prefer it over search
            const targetTicker = storedTicker || search;

            navigate(`/chartSearch/${targetTicker}`);

            // ✅ Reset after use
            if (storedTicker) {
              localStorage.removeItem("selectedTicker");
            }
        }

    }
  };

  const searchAPi = async () => {
    try {
      let res;
      let res1;
      let res2;

      if (
        (search && window.location.pathname === "/feed") ||
        window.location.pathname.includes("/feeds") ||
        window.location.pathname === `/${slug}/feeds`
      ) {
        // setLoader(true);
        console.log(search, "search is  ----------------------------------- ");
        console.log(typeof search, "Type of search");
        res = await getAllPost(search || "");
        console.log(res, "res is ---");
        if (res?.data?.data?.data) {
          setisDataFeteched(true);
          setApiCalled(false);
          setStocksData(res?.data?.data.data);
        } else if (res?.data?.data.length === 0) {
          // toast.error(`No stocks found for this keyword`);
          toast.error(
            "Recent price data is unavailable for this asset."
          );
        } else {
          toast.error("Could not search right now. Try again.");
        }

        setLoader(false);
      } else {
        const rawQuery = searchRef.current; // get and trim user input
        let formattedQuery = rawQuery;
        if (category === "Forex") {
          if (!formattedQuery.startsWith("C:")) {
            formattedQuery = `C:${formattedQuery}`;
          }
        } else if (category === "Crypto") {
          if (!formattedQuery.startsWith("X:")) {
            formattedQuery = `X:${formattedQuery}`;
          }
        }
        const query = formattedQuery;
        if (

            window.location.pathname === "/auth/trending-stocks"
        ) {
          //Watshlist
          setApiCalled(true);
          setLoader(true);
          res1 = await searchStocksApi({ search: query.toUpperCase() || "", category: category,});;
          if (res1 && res1?.data.status === 200) {
            setisDataFeteched(true);
            setStocksData(res1?.data?.data);
          } else if (res1?.data?.data.length === 0) {
            toast.error("No matching assets.");
          } else {
            toast.error("Could not search right now. Try again.");
          }
          setApiCalled(false);
          setLoader(false);
          // comment done for watchlist work
        } else if (
          !apiCalled &&
          // location.pathname !== `/${slug}/buysell` &&
          !location.pathname.startsWith("/buysell") &&
          location.pathname !== `/${slug}/charts` &&
          pathSegments[2]
        ) {
          if (pathSegments[1] === "search") {
            setLoader(true);
          }
          setApiCalled(true);
          if (search.length > 0) {
            res1 = await searchStocksApi({
              search: query.toUpperCase() || "",
              category: category,
            });
            if (res1 && res1?.data.status === 200) {
              setisDataFeteched(true);
              if (pathSegments[1] === "search") {
                setStocksData(res1?.data?.data);
              }
            } else if (res1?.data?.data.length === 0) {
              //    toast.error(`No stocks found for this keyword`);
              toast.error(
                "Recent price data is unavailable for this asset."
              );
            } else {
              toast.error("Could not search right now. Try again.");
            }
          }
          setApiCalled(false);
          setLoader(false);
        } else if (
          !apiCalled &&
          search &&
          (window.location.pathname === "/charts" ||
            // window.location.pathname === "/buysell" ||
            location.pathname.startsWith("/buysell") ||
            location.pathname === `/${slug}/buysell` ||
            location.pathname === `/${slug}/charts`)
        ) {
          setLoader(true);
          res2 = await searchStocksApi({
            search: query.toUpperCase() || "",
            category: category,
          });
          if (res2?.data?.data.length > 0) {
            localStorage.setItem("ticker", query);
            // localStorage.setItem("category", category);
            const myCustomEvent = new CustomEvent("tickerEvent", {
              detail: {
                ticker: res2?.data?.data[0]?.stock_symbol,
              },
            });
            window.dispatchEvent(myCustomEvent);
            setisDataFeteched(true);
          } else if (res2?.data?.data.length === 0) {
            //  toast.error(`No stocks found for this keyword`);
            toast.error(
              "Recent price data is unavailable for this asset."
            );
          } else {
            toast.error("Could not search right now. Try again.");
          }
          setApiCalled(false);
          setLoader(false);
        }
      }
    } catch (error) {
      // console.log("An error occurred: " + error.message);
    }
  };

  const clearSearch = () => {
    search.current.value = "";
    setScButton(false);
  };

  useEffect(() => {
    i18n.changeLanguage(selectedLanguageOption);
  }, [selectedLanguageOption]);

  // Unread marker for the top-bar glyphs. ONE token means unread everywhere
  // (#004C50 — same dot as an unread chat row); green is reserved for live
  // presence. It appears when there is something new and disappears once the
  // screen has been opened (the screen clears its badge on mount).
  const UnreadDot = () => (
    <span
      aria-hidden="true"
      className="absolute top-[-3px] right-[-3px] rounded-full border-2 border-white bg-unread"
      style={{ width: "12px", height: "12px" }}
    />
  );

  const notificationsGlyph = () => (
    <Link
      to={notificationsHref}
      aria-label={number > 0 ? "Notifications, new" : "Notifications"}
      className="flex items-center justify-center relative w-6 h-6"
      id={isMobileView ? "feed-guide-notifications" : undefined}
    >
      <img
        style={{ width: "22px", height: "26px" }}
        src={require("../../images/notification-qushi.png")}
        alt=""
      />
      {number > 0 && <UnreadDot />}
    </Link>
  );

  const chatsGlyph = () => (
    <Link
      to={chatsHref}
      aria-label={messageCount > 0 ? "Chats, new messages" : "Chats"}
      className="flex items-center justify-center relative w-6 h-6"
      id={isMobileView ? "feed-guide-messages" : undefined}
    >
      <img
        style={{ width: "26px", height: "26px" }}
        src={require("../../images/msg-qushi.png")}
        alt=""
      />
      {messageCount > 0 && <UnreadDot />}
    </Link>
  );

  const showAllIcons = () => (
    <div className="flex items-center gap-4">
      {location.pathname === "/feed" && typeof onFeedSearch === "function" && (
        <button
          type="button"
          onClick={onFeedSearch}
          aria-label={feedSearchActive ? "Close search" : "Open search"}
          className="flex items-center justify-center cursor-pointer text-slate-700 w-6 h-6"
        >
          <FontAwesomeIcon
            icon={feedSearchActive ? faTimes : faMagnifyingGlass}
            style={{ width: "22px", height: "22px" }}
          />
        </button>
      )}
      {/* Your notifications belong to your own chrome: on a profile screen the
          bell shows on YOUR profile only, never on someone else's. */}
      {(!isProfileScreen || isOwnProfileScreen) && notificationsGlyph()}
       {/* 5 */}
      {/* Chats glyph drops out on /feed (the search icon takes its slot) and on
          the Chats screens themselves (it would point at the current screen). */}
      {location.pathname !== "/feed" && !isChatsScreen && chatsGlyph()}
      {/* 6 — the competition landing page shares instead of opening a menu. */}
      {isPreJoinLanding ? (
        shareGlyph()
      ) : (
        <p
          className="flex items-center justify-center relative w-6 h-6 cursor-pointer"
          onClick={toggle}
        >
          <FontAwesomeIcon
            icon={faEllipsisVertical}
            style={{ width: "24px", height: "24px" }}
          />
        </p>
      )}
    </div>
  );

  const showChatAndToggleOnly = () => (
    <div className="flex items-center gap-4 cursor-pointer mr-[10px]">
      {!isChatsScreen && chatsGlyph()}
      {isPreJoinLanding ? (
        shareGlyph()
      ) : (
        <p
          className="flex items-center gap-4 relative flex-center"
          onClick={toggle}
        >
          <FontAwesomeIcon
            icon={faEllipsisVertical}
            style={{ width: "24px", height: "24px" }}
          />
        </p>
      )}
    </div>
  );

  // Where the header's right-hand slot is a SHARE control rather than the
  // three-dot menu. Client: "Right: share icon — landing pages only."
  //
  // The details preview joins the list: it is the landing page without the CTA,
  // and the client's note for it is "Mobile is the arrow QUSHi logo and share
  // icon". It shares only when the league has actually resolved — the message
  // carries the competition's name, and without it leagueShareData would send
  // "a QUSHi competition" instead. At the standalone address the league context
  // is deliberately not loaded (that URL must not turn the league shell on), so
  // the preview there falls back to the share control in the content column.
  const isPreJoinLanding =
    /^\/contest\/join\/[^/]+$/.test(location.pathname) ||
    location.pathname === "/contest/private-league-preview" ||
    // MOBILE ONLY for the preview. On desktop the client asked for "QUSHi logo,
    // notifications, and chats icons" in the header and the share icon moved
    // DOWN into the content column, which is where LeaguePlayTopBar puts it —
    // so this must not also put one in the bar at that width.
    // An explicitly supplied `shareGame` is sufficient ON ITS OWN. The derived
    // condition below it could not speak for the standalone preview: that path
    // is not a league path, so `leagueContextGame` is null there, and the
    // right-hand slot was falling through to the league three-dot branch and
    // rendering nothing at all. The prop is passed by exactly one screen
    // (LeagueDetails, standalone only) and only when sharing is permitted, so
    // trusting it cannot widen the share icon to any other route.
    // NO GAME REQUIREMENT. Requiring a resolved competition here is what left
    // the standalone preview (/leaderboards/<slug>/details) with no share icon
    // at all: that path is deliberately not a league path, so `leagueContextGame`
    // is null, and the right-hand slot fell through to the league three-dot
    // branch, which renders nothing on that screen. The client asked for every
    // landing header to be identical — "Mobile is the arrow QUSHi logo and share
    // icon" — so the icon is driven by the ROUTE, and the message falls back to
    // the current URL when the competition is not known (see shareThisPage).
    (isMobileView && isLandingPreview) ||
    (isLeagueHome && !inLeagueShell);

  const shareThisPage = async () => {
    // Was `document.title`, which is the static "Qushi" from index.html — so the
    // message read "Join me in Qushi on QUSHi". The league's own record carries
    // the real name.
    // The whole league record, not just its name: leagueShareData reads the type
    // off it to pick the public, private or cash-game wording the client
    // specified. The URL stays the address actually being looked at.
    const result = await shareLink(
      leagueShareData(leagueContextGame || shareGame, window.location.href)
    );
    if (result === "copied") toast.success("League link copied");
    else if (result === "failed") toast.error("Could not copy the link");
  };

  const isApplePlatform =
    typeof navigator !== "undefined" &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

  const shareGlyph = () => (
    <p
      className="flex items-center justify-center relative w-6 h-6 cursor-pointer"
      onClick={shareThisPage}
      aria-label="Share this league"
    >
      {isApplePlatform ? (
        <ShareUpIcon className="w-[22px] h-[22px]" />
      ) : (
        <MdOutlineShare size={22} />
      )}
    </p>
  );

  // Notifications + Chats, and nothing else.
  //
  // The client's mobile League Play spec gives Portfolio, Watchlist home, the
  // opened watchlist, Leaderboard and the League Profile exactly this pair — no
  // three-dot menu, no share. showAllIcons() would add the three-dot and
  // showChatAndToggleOnly() drops the bell, so neither of the existing helpers
  // says it.
  const leagueTabIcons = () => (
    <div className="flex items-center gap-4 cursor-pointer mr-[10px]">
      {/* Each glyph drops out on its OWN screen, because it would point at where
          you already are. That is the rule the app already applies elsewhere, and
          it is exactly what the client asked for here: on Notifications the right
          side is the Chats icon, and on Chats it is the Notifications icon. */}
      {!isLeagueNotificationsScreen && notificationsGlyph()}
      {!isChatsScreen && chatsGlyph()}
    </div>
  );

  const showToggleOnly = () => {
    const withChartSearch = isChartsPath && isMobileView;
    return (
      <>
        {withChartSearch && (
          <p
            className={`cursor-pointer md:hidden text-xl text-gray-700 ${
              hideLeagueThreeDot ? "mr-[3px]" : "mr-1"
            }`}
            onClick={() => setChartSearchOpen((v) => !v)}
            aria-label={chartSearchOpen ? "Close search" : "Search symbols"}
          >
            <FontAwesomeIcon icon={chartSearchOpen ? faTimes : faMagnifyingGlass} />
          </p>
        )}
        {isPreJoinLanding ? (
          <span className={`md:hidden mr-[3px] ${withChartSearch ? "ml-2" : "ml-[3rem]"}`}>
            {shareGlyph()}
          </span>
        ) : hideLeagueThreeDot ? null : (
          <p
            className={`cursor-pointer md:hidden text-2xl mr-[3px] ${withChartSearch ? "ml-2" : "ml-[3rem]"}`}
            onClick={toggle}
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </p>
        )}
      </>
    );
  };
  useEffect(() => {
    // Check if the URL contains '/:slug/' pattern
    const isDynamicRoute =
      /\/[^/]+\/(buysell|portfolio|leaderboard|profile|charts|feeds|chatHistory|create-feed|edit-feed|followers)/.test(
        location.pathname
      );
    setIsCollapsed(isDynamicRoute);
  }, [location]);
  const isChartsPath =
  location.pathname.endsWith("/charts") ||
  location.pathname.endsWith("/trade") ||
  location.pathname.startsWith("/chartSearch/");


  return (
    <>
      <div
        className={`${
          isMobileView
            ? `${
                window.location.pathname == "/feed"
                  ? "border-none px-2 border-white sm:px-8 sm:mb-2 mb-0 py-1 flex justify-between items-center flex-wrap w-full bg-white bottom-shadow "
                  : "px-2 sm:px-8 sm:mb-2 mb-0 py-1 flex justify-between items-center flex-wrap w-full bg-white bottom-shadow border-none"
              }`
            : "px-3 sm:pr-8 mb-0 py-3 flex flex-wrap w-full justify-between items-center bg-white bottom-shadow"
        }`}
        style={{
          position: "fixed",
          minHeight: "50px",
          // Client: with the crosshair up, opening the symbol search draws the
          // chart's dashed crosshair line — and the +/-/reset zoom buttons —
          // straight across the search results.
          //
          // The results list already carries z-50, but that only ranks it INSIDE
          // this header: `position: fixed` with a z-index makes this element a
          // stacking context, so everything within it is capped at the header's
          // own 10 as far as the page is concerned. The chart's overlays are
          // separate — the crosshair lines are 10, the price badge 20, the
          // date badge 50 (Chart.js) — and the chart comes later in the DOM, so
          // even the ties paint over the header.
          //
          // Lifting the whole header above them is what puts the dropdown on
          // top, and it is done ONLY while the search is open: no other screen
          // or overlay is affected, and the header returns to 10 the moment the
          // search closes.
          zIndex: chartSearchOpen ? "60" : "10",
          WebkitUserSelect: "none",
          userSelect: "none",
          WebkitTouchCallout: "none"
        }}
      >
        <div
          className={`${
            isChartsPath && !isMobileView ? "" : "hidden"
          } md:flex md:flex-1 ${isCollapsed ? "ml-20" : "ml-64"}`}
        />

        <div className="ml-1">
          {/* Left slot. The two league-mobile rules ride on top of the existing
              path list rather than inside it: `leagueMobileWantsBack` ADDS the
              back arrow on the opened watchlist and on a league profile, and
              `leagueMobileWantsAvatar` REMOVES it from League Home, which the
              client's correction (0014788) turned into the league avatar. Every
              other screen, and every non-member, takes the list unchanged. */}
          {leagueMobileWantsBack ||
          (isMobileView && (isLeaderboardPreview || isLandingPreview)) ||
          (isMobileView &&
          !leagueMobileWantsAvatar &&
          (window.location.pathname == "/leaderboard" ||
            window.location.pathname == "/contest" ||
            window.location.pathname == "/trading-floor" ||
            // The Edge Reads (Candle Anatomy, Momentum, Structure): back arrow in place of the avatar.
            window.location.pathname == "/edge/candle-anatomy" ||
            window.location.pathname == "/edge/momentum" ||
            window.location.pathname == "/edge/structure" ||
            window.location.pathname == "/contest/my-games" ||
            window.location.pathname == "/contest/cash-leagues" ||
            window.location.pathname == "/contest/add-league" ||
            window.location.pathname == `/contest/${slug}/edit` ||
            /\/contest\/game-detail\/\d+$/.test(window.location.pathname) ||
            /^\/contest\/[^/]+$/.test(window.location.pathname) ||
            /^\/contest\/join\/[^/]+$/.test(window.location.pathname) ||
            pathSegments[2] === "create-feed" ||
            pathSegments[2] === "edit-feed" ||
            /\/mobile-watchlist\/\d+/.test(window.location.pathname) ||
            /\/buysell\/(?:[XC]:)?[A-Z0-9]+/.test(window.location.pathname) ||
            /\/mobile-children-watchlist\/\d+/.test(window.location.pathname) ||
            window.location.pathname == "/auth/trending-stocks" ||
            window.location.pathname == "/market-sentiment" ||
            window.location.pathname == "/profile" ||
            window.location.pathname == "/performance" ||
            window.location.pathname == "/followers" ||
            window.location.pathname == "/chatHistory" ||
            window.location.pathname == "/chat" ||
            window.location.pathname == "/subscriptions" ||
            window.location.pathname == "/notifications" ||
            window.location.pathname == "/settings" ||
            window.location.pathname == "/search" ||
            pathSegments[1] === "profile" ||
            pathSegments[1] === "performance" ||
            pathSegments[1] === "followers" ||
            window.location.pathname == "/saved-post" ||
            pathSegments[1] === "chartSearch" ||
            window.location.pathname == "/edit-profile" ||
            pathSegments[1] === "edit-post" ||
            // pathSegments[1] === "buysell" ||
            pathSegments[1] === "portfolio" ||
            pathSegments[1] === "chatHistory" ||
            window.location.pathname == "/create-post" ||
            window.location.pathname == "/guide" ||
            window.location.pathname == "/get-help" ||
            window.location.pathname == "/about-qushi" ||
            window.location.pathname == "/change-password" )) ? (
            <p
              onClick={() => {
                // The league-mobile back arrows. "The back arrow returns the
                // user to the league screen they came from" — history, not a
                // fixed destination. On the opened watchlist this pops the entry
                // WatchlistMobile pushed when the list was opened, and its
                // popstate handler returns to the picker, so the member lands
                // back on the list picker and stays inside the league.
                if (leagueMobileWantsBack || isLeaderboardPreview || isLandingPreview) {
                  navigate(-1);
                  return;
                }
                const menuNav = getMenuNav();
                if (
                  window.innerWidth < 768 &&
                  menuNav &&
                  window.location.pathname === menuNav.destination
                ) {
                  const { origin } = menuNav;
                  clearMenuNav();
                  sidebarToggle();
                  navigate(origin);
                  return;
                }
                if (/^\/contest\/join\/[^/]+$/.test(window.location.pathname)) {
                  navigate("/contest");
                  return;
                }
                // The three View All screens always return to League Play home.
                //
                // Client: "the back arrow stops working... Please make sure the
                // back arrow works regardless of scroll position. This behavior
                // is consistent across all three View All pages."
                //
                // They fell through to the generic `navigate(-1)` below, which
                // is one history entry — and these three screens PUSH a history
                // entry every time the tab changes (the active tab is kept in
                // `?tab=`, deliberately, so the standalone leaderboard's back
                // arrow returns to the right tab). So after switching tabs the
                // previous entry is THIS SAME SCREEN with a different tab, and
                // the arrow looks dead: the page does not change.
                //
                // A fixed destination cannot have that failure mode, and League
                // Play home is where all three View All links come from. It also
                // makes the three behave identically, which is the other half of
                // what was asked.
                if (
                  window.location.pathname === "/contest/cash-leagues" ||
                  window.location.pathname === "/contest/public-leagues" ||
                  window.location.pathname === "/contest/my-games"
                ) {
                  navigate("/contest");
                  return;
                }
                if (
                  window.location.pathname == "/leaderboard" ||
                  window.location.pathname == "/contest" ||
                  window.location.pathname == "/trading-floor" ||
                  window.location.pathname == "/edge/candle-anatomy" ||
                  window.location.pathname == "/edge/momentum" ||
                  window.location.pathname == "/edge/structure" ||
                  window.location.pathname == "/contest/my-games" ||
                  window.location.pathname == "/contest/cash-leagues" ||
                  window.location.pathname == "/contest/add-league" ||
                  window.location.pathname == `/contest/${slug}/edit` ||
                  /\/contest\/game-detail\/\d+$/.test(
                    window.location.pathname
                  ) ||
                  /^\/contest\/[^/]+$/.test(window.location.pathname) ||
                  window.location.pathname == "/auth/trending-stocks" ||
                  window.location.pathname == "/market-sentiment" ||
                  window.location.pathname == "/create-post" ||
                  window.location.pathname == "/chat" ||
                  window.location.pathname == "/settings" ||
                  window.location.pathname == "/subscriptions" ||
                  window.location.pathname == "/notifications" ||
                  window.location.pathname == "/chatHistory" ||
                  window.location.pathname == "/buysell" ||
                  window.location.pathname == "/search" ||
                  pathSegments[1] === "profile" ||
                  pathSegments[1] === "performance" ||
                  pathSegments[1] === "followers" ||
                  window.location.pathname == "/saved-post" ||
                  window.location.pathname == "/charts" ||
                  window.location.pathname == "/profile" ||
                  window.location.pathname == "/performance" ||
                  window.location.pathname == "/followers" ||
                  window.location.pathname == "/edit-profile" ||
                  pathSegments[1] === "edit-post" ||
                  pathSegments[1] === "buysell" ||
                  pathSegments[1] === "portfolio" ||
                  pathSegments[1] === "chartSearch" ||
                  pathSegments[2] === "create-feed" ||
                  pathSegments[2] === "edit-feed" ||
                  pathSegments[1] === "chatHistory" ||
                  window.location.pathname == "/cliff-notes" ||
                  window.location.pathname == "/mobile-watchlist" ||
                  /\/mobile-watchlist\/\d+/.test(window.location.pathname) ||
                  window.location.pathname == "/mobile-children-watchlist" ||
                  /\/mobile-children-watchlist\/\d+/.test(window.location.pathname) ||
                  window.location.pathname == "/guide" ||
                  window.location.pathname == "/get-help" ||
                  window.location.pathname == "/about-qushi" ||
                  window.location.pathname == "/change-password"
                ) {
                  navigate(-1);
                }
              }}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                style={{ width: "17px", height: "17px" }}
              />
            </p>
          ) : (
            <p
              className="flex-shrink-0 rounded-full block md:hidden border shadow-lg"
              /* Client: "inside league play whenever click on avatar it will move
                 to league dashboard." That is the member's own League Record —
                 the same screen the desktop sidebar's League Dashboard row opens,
                 which is why both now read the destination from one helper.
                 Outside a league the avatar keeps opening the member's main QUSHi
                 profile, exactly as it always has. */
              onClick={() =>
                inLeagueShell && leagueSlug
                  ? navigate(
                      leagueDashboardPath(
                        leagueSlug,
                        localStorage.getItem("user_id")
                      )
                    )
                  : goToMyProfile()
              }
              onContextMenu={(e) => e.preventDefault()}
              style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }}
            >
                <img
                  className="rounded-full object-cover md:h-10 md:w-10 h-10 w-10 border cursor-pointer"
                  style={{ width: "34px", height: "34px", pointerEvents: "none" }}
                  // src={avtar || localStorage.getItem("img") || "/qushi_logo.png"}
                  src={
                    isTradingDetail || (!avtar && !localStorage.getItem("img"))
                      ? "/qushi_logo.png"
                      : avtar ||
                        localStorage.getItem("img") ||
                        "/qushi_logo.png"
                  }
                  alt="Avatar"
                  draggable="false"
                />
            </p>
          )}
        </div>
        <div
          className="absolute md:left-[calc(50%+128px)] left-1/2 translate-x-[-50%] translate-y-[-50%] top-1/2"
          style={{
            WebkitUserSelect: "none",
            userSelect: "none",
            WebkitTouchCallout: "none"
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div>
            <div className="grid justify-center items-center ">
              {isChartsPath && isMobileView && chartSearchOpen ? (
                <div className="w-[64vw] max-w-[280px]">
                  <ChartMobileSearch onClose={() => setChartSearchOpen(false)} />
                </div>
              ) : (
              <img
                className="w-28 mx-auto"
                style={{
                  width: "84px",
                  height: "21px",
                  pointerEvents: "none"
                }}
                src={require("../../images/log2.png")}
                alt="Avatar"
                draggable="false"
              />
              )}
              {
              // isMobileView && window.location.pathname == "/leaderboard" ? (
              //   <span className="text-center text-black font-samibold">
              //     Leaderboard
              //   </span>
              // ) :
               isMobileView && window.location.pathname == "/contest" ? (
                /* Mobile: no page title under the QUSHi logo for League Play */
                null
              ) : isMobileView &&
                window.location.pathname == "/trading-floor" ? (
                /* Mobile: no page title under the QUSHi logo for Trading Floor */
                null
              ) : isMobileView &&
                window.location.pathname == "/contest/my-games" ? (
                /* Mobile: no page title under the QUSHi logo — League Play screens carry
                   their own header (the old text-header system is retired). */
                null
              ) : isMobileView &&
                window.location.pathname == "/contest/cash-leagues" ? (
                /* Mobile: no page title under the QUSHi logo — League Play screens carry
                   their own header (the old text-header system is retired). */
                null
              ) : isMobileView &&
                /^\/contest\/(?!add-league)[^/]+$/.test(
                  window.location.pathname
                ) ? (
                /* Mobile: no page title under the QUSHi logo — League Play screens carry
                   their own header (the old text-header system is retired). */
                null
              ) : isMobileView &&
                window.location.pathname === "/contest/add-league" ? (
                /* Mobile: no page title under the QUSHi logo — League Play screens carry
                   their own header (the old text-header system is retired). */
                null
              ) : isMobileView &&
                window.location.pathname == `/contest/${slug}/edit` ? (
                /* Mobile: no page title under the QUSHi logo — League Play screens carry
                   their own header (the old text-header system is retired). */
                null
              ) : isMobileView &&
                window.location.pathname === `/${slug}/feeds` ? (
                /* Mobile: no page title under the QUSHi logo — League Play screens carry
                   their own header (the old text-header system is retired). */
                null
              ) : isMobileView &&
                window.location.pathname === `/${slug}/buysell` ? (
                /* Mobile: no page title under the QUSHi logo — League Play screens carry
                   their own header (the old text-header system is retired). */
                null
              ) : isMobileView &&
                window.location.pathname === `/${slug}/charts` ? (
                /* Mobile: no page title under the QUSHi logo — League Play screens carry
                   their own header (the old text-header system is retired). */
                null
              ) : isMobileView &&
                window.location.pathname === `/${slug}/create-feed` ? (
                /* Mobile: no page title under the QUSHi logo — League Play screens carry
                   their own header (the old text-header system is retired). */
                null
              ) : isMobileView &&
                window.location.pathname === `/${slug}/portfolio` ? (
                /* Mobile: no page title under the QUSHi logo — League Play screens carry
                   their own header (the old text-header system is retired). */
                null
              ) : isMobileView &&
                window.location.pathname === `/${slug}/leaderboard` ? (
                /* Mobile: no page title under the QUSHi logo — League Play screens carry
                   their own header (the old text-header system is retired). */
                null
              ) : isMobileView &&
                window.location.pathname == "/auth/trending-stocks" ? (
                /* Mobile: no page title under the QUSHi logo for Today's Movers */
                null
              ) : isMobileView &&
                window.location.pathname == "/market-sentiment" ? (
                /* Mobile: no page title under the QUSHi logo for Market Sentiment */
                null
              ) : isMobileView && /\/mobile-watchlist\/\d+/.test(window.location.pathname) ? (
                  <span className="text-center text-black font-samibold">
                    {searchVal1}
                  </span>
                ):isMobileView && /\/mobile-children-watchlist\/\d+/.test(window.location.pathname) ? (
                  <span className="text-center text-black font-samibold">
                    {searchVal1}
                  </span>
                ): isMobileView && window.location.pathname == "/chat" ? (
                /* Mobile: no page title under the QUSHi logo for Chats — the
                   in-content SettingsPageHeading carries the house-rule title. */
                null
              ) : isMobileView && window.location.pathname == "/settings" ? (
                /* Mobile: no page title under the QUSHi logo for Settings */
                null
              ) : isMobileView &&
                window.location.pathname == "/subscriptions" ? (
                <span className="text-center text-black font-samibold">
                  Subscriptions
                </span>
              ) : isMobileView &&
                window.location.pathname == "/notifications" ? (
                /* Mobile: no page title under the QUSHi logo for Notifications */
                null
              ) : isMobileView &&
                window.location.pathname == "/buysell" &&
                location.pathname == `/${slug}/buysell` ? (
                <span className="text-center text-black font-samibold">
                  {" "}
                  Create Order
                </span>
              )
              : isMobileView && window.location.pathname == "/buysell" ? (
                <span className="text-center text-black font-samibold">
                  Buy/Sell
                </span>
              ) : isMobileView && window.location.pathname == "/chatHistory" ? (
                <span className="text-center text-black font-samibold">
                  Chat History
                </span>
              ): isMobileView && pathSegments[1] === "performance" ? (
                <span className="text-center text-black font-samibold">
                  Performance
                </span>
              ) : isMobileView && pathSegments[1] === "followers" ? (
                <span className="text-center text-black font-samibold">
                  Followers
                </span>
              ) : isMobileView && window.location.pathname == "/saved-post" ? (
                /* Mobile: no page title under the QUSHi logo for Saved */
                null
              )

              : (
                ""
              )

              }
            </div>
          </div>
        </div>

        <div
          style={{
            marginLeft:
              location.pathname === "/feed" ||
              window.location.pathname.includes("/feeds"),
          }}
          className="avaterSection flex items-center gap-2 sm:gap-6 text-slate-400"
        >
          <div
            className={`flex flex-row gap-4 justify-between ${
              isMobileView ? "" : "hidden"
            }`}
          >
            {location.pathname === "/feed" ||
            (window.location.pathname.includes("/feeds") && !isValidPath)
              ? showAllIcons()
              : ""}
            {/* League Home, for a MEMBER on a phone: the client's spec is
                "League avatar | QUSHi logo | Notifications, Chats, three dot
                menu". showAllIcons() is that trio exactly — and it keeps the
                three-dot because isPreJoinLanding is false for a member. The
                branch below used to answer this path with showToggleOnly(),
                which gave the three-dot alone. A visitor who has not joined
                still takes that branch and keeps the share glyph. */}
            {leagueMobileWantsAvatar ? showAllIcons() : null}

            {/* Portfolio, Watchlist home, the opened watchlist, Leaderboard,
                League Dashboard and another member's League Profile — all six
                get Notifications + Chats. None of them matched any branch here
                before, so the right-hand side of the league header was simply
                empty on a phone. Trade is deliberately absent: the client asked
                for "Search only" there, and the /trade branch further down
                already produces exactly that. */}
            {inLeagueMobile && isLeagueTabPath ? leagueTabIcons() : null}

            {!leagueMobileWantsAvatar &&
            ([
              "/leaderboard",
              "/contest",
              "/trading-floor",
              "/contest/my-games",
              "/contest/cash-leagues",
              "/contest/add-league",
              "/auth/trending-stocks",
              "/market-sentiment",
              "/create-post",
              "/notifications",
              "/search",
              "/charts",
              "/traderprofile",
              "/saved-post",
              "/edit-profile",
              "/mobile-watchlist",
              "mobile-children-watchlist",
               "/guide",
              "/get-help",
              "/about-qushi",
              "/change-password",
            ].includes(location.pathname) ||
            /\/contest\/game-detail\/\d+/.test(location.pathname) ||
            /^\/contest\/[^/]+$/.test(window.location.pathname) ||
            /\/mobile-watchlist\/\d+/.test(window.location.pathname) ||
            /\/mobile-children-watchlist\/\d+/.test(window.location.pathname) ||
            window.location.pathname == `/contest/${slug}/edit`)
              ? showToggleOnly()
              : null}
            {pathSegments[1] === "chartSearch" ? showToggleOnly() : ""}
            {pathSegments[2] === "trade" || pathSegments[1] === "trade"
              ? showToggleOnly()
              : ""}
            {pathSegments[1] === "edit-post" ? showChatAndToggleOnly() : ""}
            {pathSegments[1] === "paid-content" ? showChatAndToggleOnly() : ""}
            {pathSegments[1] === "profile" ? showAllIcons() : ""}
            {pathSegments[1] === "buysell" ? showToggleOnly() : ""}
            {pathSegments[1] === "chatHistory" ? showChatAndToggleOnly() : ""}
            {pathSegments[1] === "portfolio" ? showChatAndToggleOnly() : ""}

            {["/subscriptions", "/settings", "/chat"].includes(
              location.pathname
            ) && showChatAndToggleOnly()}
          </div>
          {isMobileView || inLeagueShell ? null : (
            <div
              onClick={goToMyProfile}
              onContextMenu={(e) => e.preventDefault()}
              style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", cursor: "pointer" }}
            >
              <img
                className="object-cover rounded-full md:h-10 md:w-10 h-10 w-10 border cursor-pointer"
                src={
                  isTradingDetail || (!avtar && !localStorage.getItem("img"))
                    ? "/qushi_logo.png"
                    : avtar || localStorage.getItem("img") || "/qushi_logo.png"
                }
                alt="Avatar"
                draggable="false"
                style={{ pointerEvents: "none" }}
              />
            </div>
          )}

          <div className="hidden md:flex flex-row items-center gap-4 text-xl">
            {/* Same two rules as mobile: the bell is your own chrome (own
                profile only), and the Chats glyph drops out on the Chats
                screens because it points at where you already are. */}
            {(!isProfileScreen || isOwnProfileScreen) &&
              !isLeagueNotificationsScreen && (
              <Link
                to={notificationsHref}
                aria-label={number > 0 ? "Notifications, new" : "Notifications"}
                className="relative flex items-center justify-center"
                id={!isMobileView ? "feed-guide-notifications" : undefined}
              >
                <img
                  style={{ width: "22px", height: "26px" }}
                  src={require("../../images/notification-qushi.png")}
                  alt=""
                />
                {number > 0 && <UnreadDot />}
              </Link>
            )}
             {/* 5 */}
            {!isChatsScreen && (
              <Link
                to={chatsHref}
                aria-label={
                  messageCount > 0 ? "Chats, new messages" : "Chats"
                }
                className="relative flex items-center justify-center"
                id={!isMobileView ? "feed-guide-messages" : undefined}
              >
                <img
                  style={{ width: "26px", height: "26px" }}
                  src={require("../../images/msg-qushi.png")}
                  alt=""
                />
                {messageCount > 0 && <UnreadDot />}
              </Link>
            )}
             {/* 6 */}
          </div>
        </div>
      </div>
      {isChartsPath && isMobileView && (
        <div className={`pl-2 ${
    isMobileView && isChartsRoute ? "pt-12" : "pt-16"
  } md:flex md:flex-1 justify-center`}>
          {!shouldHideTopbarSearch && !isMobileView && (
            <TopbarSearch
              clearSearch={clearSearch}
              handleChange={handleChange}
              handleCategory={handleCategory}
              scButton={scButton}
              search={search}
              handleSubmit={handleSubmit}
              isMobileView
            />
          )}
        </div>
      )}
    </>
  );
}

export default DashboardHeader;
